import React, { useCallback, useEffect, useMemo, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import { TextField, Button, makeStyles, DialogActions, Typography } from "@material-ui/core";
import { useDispatch } from "react-redux";
import FormSelect from "./formSelect";
import { getQuestionComponentByKey } from "../activityQuestions/components/questions/QuestionComponentMap";
import useActivityQuestions from "../activityQuestions/hooks/useActivityQuestions";
import { createUUId } from "../../utils/uuid";
import { requestActivityQuestions } from "../../redux/activityQuestions/activityQuestionsActions";
import SingleColumnContainer from "../activityQuestions/components/followupGridContainer/signleColumnContainer";
import { customOrderQuestionsWithDependsOn } from "../../utils/customQuestionsOrderWithDependsOn";
import { processQuestionVisibilityAndMandatory } from "../activityQuestions/utils/mandatoryQuestions";
import useCheckDuplicateWorkOrder from "../../hooks/useCheckDuplicateWorkOrder";
import useIsEndToEndEnabled from "../../hooks/useIsEndToEndEnabled";

// Custom form component for adding an object like Follow-ups (multiple field filled in one pop-up)
const messages = defineMessages({
    followupCommentHeader: {
        id: "admin.work-order-details.follow-up-list.comment-col-header",
        defaultMessage: "Comments",
        description: "Admin work order follow-up comment column header",
    },
});

const useStyles = makeStyles((theme) => ({
    stepperNext: {
        marginLeft: "auto",
        marginRight: 0,
        display: "block",
    },
    stepperPrev: {
        marginRight: "auto",
        marginLeft: 0,
        display: "block",
    },
    addNew: {
        height: "70px",
        display: "flex",
        paddingLeft: "13px",
    },
    characterCount: {
        float: "right",
        marginTop: "-20px",
        marginRight: "15px",
        fontSize: "10px",
        color: "#999999",
    },
    commentText: {
        color: "#000",
        opacity: 0.5,
        wordBreak: "normal",
    },
}));

const COMMENTLENGTH = 300;

export const AddFollowUp = (props) => {
    const { reasons, onDone, followUp = null } = props;
    const intl = useIntl();
    const classes = useStyles();
    const dispatch = useDispatch();

    const { activityQuestions, endToEndTypes } = useActivityQuestions();

    const [followUpReason, setFollowUpReason] = useState(followUp ? followUp : null);
    const [comments, setComments] = useState(followUp ? followUp.comments : "");
    const [uuid, _setUuid] = useState(followUp ? followUp.uuid : createUUId());
    const [currentStep, setCurrentStep] = useState(1);
    const [totalSteps, setTotalSteps] = useState(2);
    const [filteredReasons, setFilteredReasons] = useState(reasons);
    const { checkDuplicateWorkOrder } = useCheckDuplicateWorkOrder();
    const isEndToEndEnabled = useIsEndToEndEnabled(props.workorderData.workOrderNumber);

    const questions = useMemo(() => activityQuestions?.questions?.[uuid]?.mappedQuestions ?? null, [activityQuestions?.questions, uuid]);
    const orderedQuestions = useMemo(() => customOrderQuestionsWithDependsOn(questions ?? []), [questions]);
    const processedQuestions = useMemo(() => {
        return orderedQuestions
            .map((question) => {
                const { dependencyCheck, isVisible, mandatoryCheck, isMandatory } = processQuestionVisibilityAndMandatory(question, orderedQuestions);

                return {
                    question,
                    dependencyCheck,
                    mandatoryCheck,
                    isVisible,
                    isMandatory,
                };
            })
            .filter((processed) => processed.isVisible);
    }, [orderedQuestions]);

    useEffect(() => {
        if (!isEndToEndEnabled) {
            setFilteredReasons(reasons.filter((reason) => !endToEndTypes.includes(reason.followUpTypeID)));
        }
    }, [isEndToEndEnabled, reasons]);

    useEffect(() => {
        // increase steps to include the additional question,
        // if followup reason changed in step 1, resets steps to 2.
        if (followUpReason?.hasActivityQuestions && processedQuestions) {
            setTotalSteps(Object.keys(processedQuestions).length + 2);
        } else {
            setTotalSteps(2);
        }
    }, [processedQuestions, followUpReason]);

    const handleDone = useCallback(() => {
        onDone(followUpReason, comments, uuid);
    }, [onDone, followUpReason, comments]);

    const stepper = useMemo(() => {
        return () => {
            const question =
                currentStep > 1 && processedQuestions
                    ? {
                          mandatory: processedQuestions[currentStep - 2]?.isMandatory,
                          value: processedQuestions[currentStep - 2]?.question?.value,
                      }
                    : { mandatory: true, value: followUpReason?.followUpTypeID?.toString() };

            return (
                <DialogActions>
                    {currentStep !== 1 && (
                        <Button className={classes.stepperPrev} tertiary onClick={() => setCurrentStep(currentStep - 1)}>
                            <FormattedMessage
                                id="wo-actions.edit-popup.previous"
                                defaultMessage="&lt; Previous"
                                description="Previous Button label"
                            />
                        </Button>
                    )}
                    {currentStep >= 1 && currentStep < totalSteps && (
                        <Button
                            className={classes.stepperNext}
                            disabled={question?.mandatory ? !(question?.value?.length > 0) : false}
                            tertiary
                            onClick={() => setCurrentStep(currentStep + 1)}>
                            <FormattedMessage id="wo-actions.edit-popup.next" defaultMessage="Next &gt;" description="Next Button label" />
                        </Button>
                    )}
                    {currentStep === totalSteps && (
                        <Button className={classes.stepperNext} tertiary onClick={handleDone}>
                            <FormattedMessage id="wo-actions.edit-popup.done" defaultMessage="Done" description="Done Button label" />
                        </Button>
                    )}
                </DialogActions>
            );
        };
    }, [followUpReason, currentStep, totalSteps, processedQuestions, handleDone]);

    const followUPHandler = useCallback(
        (reason) => {
            if (reason.followUpTypeID && endToEndTypes.includes(Number(reason.followUpTypeID)) && uuid) {
                dispatch(requestActivityQuestions(Number(reason.workActivityID), uuid));
            }
            setFollowUpReason(reason);
            if (reason.newWorkOrderRequired)
                checkDuplicateWorkOrder(
                    {
                        followUpTpyeId: reason.followUpTypeID,
                        comments: comments,
                        uuid: uuid,
                        hasActivityQuestions: reason.hasActivityQuestions,
                        dueDate: reason?.leadTime ? moment(new Date())?.businessAdd(reason?.leadTime)._d : moment(new Date()),
                        validatedForDoubles: false,
                        new: true,
                    },
                    [],
                    "labourer",
                    false,
                    { isPopupOpen: true },
                );
        },
        [dispatch, endToEndTypes, uuid, props.workorderData, comments],
    );

    const fieldToShow = useCallback(() => {
        if (currentStep === 1) {
            // its first step
            return (
                <FormSelect
                    required
                    options={filteredReasons}
                    existingValue={
                        followUp?.id || followUpReason?.followUpTypeID
                            ? filteredReasons.find((r) => [followUp?.id, followUpReason?.followUpTypeID].includes(r.followUpTypeID))
                            : null
                    }
                    onSelect={followUPHandler}
                />
            );
        } else if (currentStep === totalSteps) {
            // its a last step
            return (
                <>
                    <Typography variant="body1" className={classes.commentText}>
                        {intl.formatMessage(messages.followupCommentHeader)}
                    </Typography>
                    <TextField
                        className={classes.root}
                        textareastyle
                        variant="outlined"
                        InputProps={{
                            inputProps: {
                                maxLength: COMMENTLENGTH,
                            },
                        }}
                        fullWidth
                        multiline
                        maxRows={4}
                        value={comments}
                        onChange={(event) => {
                            setComments(event.target.value);
                        }}
                    />
                    <div className={classes.characterCount}>{comments ? `${comments.length}/${COMMENTLENGTH}` : `0/${COMMENTLENGTH}`}</div>
                </>
            );
        } else if (currentStep && processedQuestions && processedQuestions.length >= currentStep - 2) {
            const { question, isMandatory = false } = processedQuestions[currentStep - 2] ?? {};
            return question
                ? getQuestionComponentByKey(
                      question.questionType,
                      { question: { ...question, mandatory: isMandatory }, followUpUUID: uuid },
                      SingleColumnContainer,
                  )
                : null;
        }
        return null;
    }, [currentStep, totalSteps, processedQuestions, comments, followUp, filteredReasons, followUPHandler, uuid, followUpReason]);

    return (
        <>
            <div>{fieldToShow()}</div>
            <div>{stepper()}</div>
        </>
    );
};

export default AddFollowUp;
